import { useEffect, useMemo, useRef, useState } from 'react';
import {
  computeEffectiveStatus,
  R9B_EMPTY_DEBOUNCE_MS,
  type EffectiveStatusResult,
} from '../utils/effectiveRoomStatus';
import type { RoomResponse } from '../types/auth';

export interface UseEffectiveRoomStatusInput {
  room: RoomResponse | null | undefined;
  /**
   * SignalR participants — present in LiveRoomScreen, absent (null) on
   * dashboard. When null, R9b is skipped and BE-aggregated fields are used
   * for R3/R9a.
   */
  participants?: Array<{ userId: string; isHost: boolean }> | null;
}

export interface UseEffectiveRoomStatusResult extends EffectiveStatusResult {
  /** True iff the host is currently in `participants`. */
  hostPresent: boolean;
  /** Current viewer count (excludes host). */
  viewerCount: number;
}

const DASHBOARD_TICK_MS = 30_000;
const ENDING_SOON_TICK_MS = 1_000;

export function useEffectiveRoomStatus(
  input: UseEffectiveRoomStatusInput,
): UseEffectiveRoomStatusResult {
  const { room, participants = null } = input;

  // Local trackers — only meaningful when participants is provided.
  const liveViewerCountAllTime = useRef<number | null>(participants != null ? 0 : null);
  const liveHostJoinedAllTime = useRef<boolean | null>(participants != null ? false : null);
  const participantsEmptySinceRef = useRef<number | null>(null);
  const hostAbsentSinceRef = useRef<number | null>(null);

  useEffect(() => {
    if (participants == null) {
      // Dashboard mode: nothing to track.
      liveViewerCountAllTime.current = null;
      liveHostJoinedAllTime.current = null;
      participantsEmptySinceRef.current = null;
      hostAbsentSinceRef.current = null;
      return;
    }
    if (liveViewerCountAllTime.current == null) liveViewerCountAllTime.current = 0;
    if (liveHostJoinedAllTime.current == null) liveHostJoinedAllTime.current = false;
    const hostId = room?.hostUserId;
    const hostHere = hostId ? participants.some((p) => p.userId === hostId) : false;
    const viewersNow = participants.filter((p) => p.userId !== hostId).length;
    liveViewerCountAllTime.current = Math.max(liveViewerCountAllTime.current, viewersNow);
    if (hostHere) {
      liveHostJoinedAllTime.current = true;
      hostAbsentSinceRef.current = null;
    } else if (
      liveHostJoinedAllTime.current === true &&
      hostAbsentSinceRef.current == null
    ) {
      // Host previously joined but has stepped away — start the debounce clock.
      hostAbsentSinceRef.current = Date.now();
    }
    if (participants.length === 0) {
      if (participantsEmptySinceRef.current == null) {
        participantsEmptySinceRef.current = Date.now();
      }
    } else {
      participantsEmptySinceRef.current = null;
    }
  }, [participants, room?.hostUserId]);

  const [now, setNow] = useState(() => Date.now());

  const result = useMemo<UseEffectiveRoomStatusResult>(() => {
    if (!room) {
      return {
        effectiveStatus: 'scheduled',
        secondsUntilAutoEnd: null,
        hostPresent: false,
        viewerCount: 0,
      };
    }
    const emptySince = participantsEmptySinceRef.current;
    const participantsEmptyForMs = emptySince != null ? Math.max(0, now - emptySince) : null;
    const hostAbsentSince = hostAbsentSinceRef.current;
    const hostAbsentForMs = hostAbsentSince != null ? Math.max(0, now - hostAbsentSince) : null;
    const computed = computeEffectiveStatus({
      room,
      participants: participants ?? null,
      liveViewerCountAllTime: liveViewerCountAllTime.current,
      liveHostJoinedAllTime: liveHostJoinedAllTime.current,
      participantsEmptyForMs,
      hostAbsentForMs,
      now,
    });
    const hostPresent = !!participants?.some((p) => p.userId === room.hostUserId);
    const viewerCount = participants
      ? participants.filter((p) => p.userId !== room.hostUserId).length
      : 0;
    return { ...computed, hostPresent, viewerCount };
  }, [room, participants, now]);

  // Coarse 30s ticker — always running. Cheap.
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), DASHBOARD_TICK_MS);
    return () => clearInterval(id);
  }, []);

  // Fine 1s ticker — only while ending_soon, for the countdown banner.
  useEffect(() => {
    if (result.effectiveStatus !== 'ending_soon') return;
    const id = setInterval(() => setNow(Date.now()), ENDING_SOON_TICK_MS);
    return () => clearInterval(id);
  }, [result.effectiveStatus]);

  return result;
}
